import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { lazy, Suspense, useMemo } from 'react';
import { endOfYear, startOfYear } from 'date-fns';
import Decimal from 'decimal.js';
import { compact } from 'lodash-es';
import { Spinner, Typography, WithInfoIcon } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import GenericError from '~/components/GenericError/GenericError';
import { DateRange } from '~/components/TableFilters/types';
import { routes } from '~/constants';
import { useIoContext, useStore } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { RevenueStatus, HistoricRevenueModel } from '~/models/HistoricRevenueModel';
import { toDatestampArray } from '~/models/time';
import { selectors } from '~/store/state-selectors';
import { formatNumericUSDate } from '~/utils';
import { getLastXMonths } from '~/utils/date.utils';
import GraphWidgetBottomCard from '../GraphWidget/GraphWidgetBottomCard';
import GraphWidgetLayout from '../GraphWidget/GraphWidgetLayout';
import GraphWidgetTopCard from '../GraphWidget/GraphWidgetTopCard';
import { usePayoutsSummary } from '../PayoutsSummaryWidget/hooks/usePayoutsSummary';
import { useHistoricRevenueStats } from './hooks/useHistoricRevenueStats';
import styles from './RevenueWidget.module.scss';
import { revenueWidgetTextsPath } from './RevenueWidget.utils';
const HistoricRevenueChartInner = /*#__PURE__*/ lazy(()=>import(/* webpackChunkName: "recharts" */ './HistoricRevenueChartInner').then((module)=>({
            default: module.HistoricRevenueChartInner
        })));
export const GetRevenueWidgetTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            RevenueWidgetTID: {
                EarnedThisYear: 'revenue-stats-earned-this-year',
                ExpectedThisYear: 'revenue-stats-expected-this-year',
                ChartPlaceholder: 'revenue-chart-placeholder',
                Root: 'revenue-stats',
                Chart: 'revenue-stats-chart',
                WidgetError: 'widget-error'
            }
        }
    });
const topCards = (t, historicRevenueStats, loading, now, earnedThisYearTestId, expectedThisYearTestId)=>compact([
        /*#__PURE__*/ _jsx(TopCard, {
            testId: earnedThisYearTestId,
            title: t.earnedThisYear.title(),
            titleInfo: t.earnedThisYear.tooltip(),
            primaryText: historicRevenueStats.totalThisYear(),
            linkTo: "".concat(routes.revenueReport, "?dateRange=").concat(formatNumericUSDate(startOfYear(now)), ",").concat(formatNumericUSDate(endOfYear(now)), "&dateRangeOption=").concat(DateRange.CurrentYear),
            loading: loading
        }, "earned-this-year"),
        /*#__PURE__*/ _jsx(TopCard, {
            testId: expectedThisYearTestId,
            title: t.expectedThisYear.title(),
            titleInfo: t.expectedThisYear.tooltip(),
            primaryText: historicRevenueStats.totalExpectedThisYear(),
            linkTo: routes.futureInvoicesReport,
            loading: loading
        }, "expected-this-year")
    ]);
function HistoricRevenueChart(param) {
    let { data, testId, isTestMode, legendLabels } = param;
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": testId,
        className: styles.chart,
        children: /*#__PURE__*/ _jsx(Suspense, {
            fallback: /*#__PURE__*/ _jsx(Spinner, {
                size: 30
            }),
            children: /*#__PURE__*/ _jsx(HistoricRevenueChartInner, {
                data: data,
                testId: testId,
                isTestMode: isTestMode,
                legendLabels: legendLabels
            })
        })
    });
}
const PlaceholderChart = (props)=>{
    const { getNow } = useIoContext();
    const { RevenueWidgetTID } = useMemo(()=>GetRevenueWidgetTID(), []);
    const placeholderData = getLastXMonths(getNow(), 12).map((date)=>({
            date,
            [RevenueStatus.Paid]: {
                date: toDatestampArray(date),
                totalAmount: new Decimal(4000)
            },
            [RevenueStatus.Expected]: {
                date: toDatestampArray(date),
                totalAmount: new Decimal(1000)
            },
            [RevenueStatus.PaidExternally]: {
                date: toDatestampArray(date),
                totalAmount: new Decimal(4000)
            }
        }));
    return /*#__PURE__*/ _jsx(HistoricRevenueChart, _object_spread({
        testId: RevenueWidgetTID.ChartPlaceholder(),
        data: placeholderData
    }, props));
};
const TopCard = (param)=>{
    let { title, testId, linkTo, success, loading, titleInfo = '', primaryText } = param;
    return /*#__PURE__*/ _jsx(GraphWidgetTopCard, {
        linkTo: linkTo,
        children: /*#__PURE__*/ _jsxs("div", {
            "data-testid": loading ? "".concat(testId, "-loading") : testId,
            className: styles.topCard,
            children: [
                /*#__PURE__*/ _jsx("div", {
                    children: /*#__PURE__*/ _jsx(WithInfoIcon, {
                        showInfo: !!titleInfo,
                        info: titleInfo,
                        children: /*#__PURE__*/ _jsx(Typography, {
                            skin: success ? 'success' : 'standard',
                            variant: "paragraph1",
                            ellipsis: true,
                            children: title
                        })
                    })
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    weight: "normal",
                    variant: "h5",
                    testId: testId && "".concat(testId, "-primary"),
                    children: loading ? /*#__PURE__*/ _jsx(Spinner, {
                        size: 30
                    }) : primaryText
                })
            ]
        })
    });
};
const RevenueWidgetContent = (props)=>{
    const { loading, historicRevenueStats, payouts, isTestMode, loadingOrEmptyBg } = props;
    const { t } = useLocaleKeys(revenueWidgetTextsPath);
    const { getNow } = useIoContext();
    const now = useMemo(()=>getNow(), []);
    const { RevenueWidgetTID } = useMemo(()=>GetRevenueWidgetTID(), []);
    const data = useMemo(()=>historicRevenueStats.chartData(), [
        historicRevenueStats
    ]);
    return /*#__PURE__*/ _jsxs(GraphWidgetLayout, {
        title: t.title(),
        loading: loading,
        bottomHeight: "l",
        children: [
            topCards(t, historicRevenueStats, loading, now, RevenueWidgetTID.EarnedThisYear(), RevenueWidgetTID.ExpectedThisYear()),
            /*#__PURE__*/ _jsx(GraphWidgetBottomCard, {
                empty: !payouts.hasEverHadPaidPayout() && !historicRevenueStats.hasRevenueThisYear() && !historicRevenueStats.hasExpectedRevenue(),
                emptyMsg: t.emptyMsg(),
                testId: RevenueWidgetTID.Root(),
                loadingOrEmptyBg: loadingOrEmptyBg,
                children: /*#__PURE__*/ _jsx(HistoricRevenueChart, {
                    testId: RevenueWidgetTID.Chart(),
                    data: data,
                    isTestMode: isTestMode,
                    legendLabels: t.legendLabels
                })
            })
        ]
    });
};
const RevenueWidget = (param)=>{
    let { isTestMode } = param;
    const { historicRevenueStats, isLoading: revenueLoading, isFetching: revenueFetching, error: revenueError } = useHistoricRevenueStats({
        refreshOnHookMount: true
    });
    const { payoutsSummary, isLoading: payoutsLoading, isFetching: payoutsFetching, error: payoutsError } = usePayoutsSummary({
        refreshOnHookMount: true
    });
    const isMemberUser = useStore(selectors.user.isMemberUser);
    const { getNow } = useIoContext();
    const { t } = useLocaleKeys(revenueWidgetTextsPath);
    const now = useMemo(()=>getNow(), []);
    const { RevenueWidgetTID } = useMemo(()=>GetRevenueWidgetTID(), []);
    const loadingOrEmptyBg = ()=>/*#__PURE__*/ _jsx(PlaceholderChart, {
            isTestMode: isTestMode,
            legendLabels: t.legendLabels
        });
    const isLoading = revenueLoading || payoutsLoading;
    const isFetching = revenueFetching || payoutsFetching;
    const error = revenueError || payoutsError;
    if (isMemberUser) return null;
    if (error) return /*#__PURE__*/ _jsx(GenericError, {
        testId: RevenueWidgetTID.WidgetError()
    });
    if (isLoading) {
        return /*#__PURE__*/ _jsxs(GraphWidgetLayout, {
            title: t.title(),
            loading: true,
            bottomHeight: "l",
            children: [
                topCards(t, new HistoricRevenueModel([], [], [], new Decimal(0), now), true, now, RevenueWidgetTID.EarnedThisYear(), RevenueWidgetTID.ExpectedThisYear()),
                /*#__PURE__*/ _jsx(GraphWidgetBottomCard, {
                    loading: true,
                    loadingMsg: t.loadingMsg(),
                    loadingOrEmptyBg: loadingOrEmptyBg
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsx(RevenueWidgetContent, {
        loading: isFetching,
        payouts: payoutsSummary,
        isTestMode: isTestMode,
        historicRevenueStats: historicRevenueStats,
        loadingOrEmptyBg: loadingOrEmptyBg
    });
};
export default RevenueWidget;
